import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAtom } from "jotai";
import { Dropdown, Field } from "../components/form";
import { useUser } from "../store";
import { usersState } from "../store/users";
import { notify } from "../util/notify";
import { UserType } from "common";
import "./EditUser.scss";

const UserForm = ({
  handleSubmit,
  onSubmit,
  errors,
  register,
  users,
  disableUsername,
  defaultValues,
}: any) => {
  return (
    <form className="user-form-card" onSubmit={handleSubmit(onSubmit)}>
      <section className="user-form-section">
        <div className="user-form-section-heading">
          <span className="user-form-section-icon">
            <i className="fa-solid fa-id-card" aria-hidden="true" />
          </span>
          <div>
            <h2>Account details</h2>
            <p>Set the user’s identity and sign-in information.</p>
          </div>
        </div>
        <div className="user-form-grid">
          <Field
            name="username"
            errors={errors}
            register={register}
            disabled={disableUsername}
          />
          <Field name="name" errors={errors} register={register} />
          <Field name="email" errors={errors} register={register} type="email" />
        </div>
      </section>

      <section className="user-form-section">
        <div className="user-form-section-heading">
          <span className="user-form-section-icon">
            <i className="fa-solid fa-shield-halved" aria-hidden="true" />
          </span>
          <div>
            <h2>Access and preferences</h2>
            <p>Choose where the user can work and which updates they receive.</p>
          </div>
        </div>
        <div className="user-form-access-grid">
          {users?.facilities && (
            <Dropdown
              name="facilities"
              errors={errors}
              register={register}
              options={users?.facilities}
              multiple={true}
              selected={users?.facilities}
            />
          )}
          <Dropdown
            name="type"
            errors={errors}
            register={register}
            options={Object.keys(UserType)}
            selected={"Regular"}
          />
        </div>
        <div className="field user-daily-reports-field">
          <div className="control">
            <label className="checkbox receive-daily-reports">
              <input
                type="checkbox"
                {...register("receiveDailyReports")}
              />
              <span>
                <strong>Receive daily reports</strong>
                <small>Include this user in scheduled report emails.</small>
              </span>
            </label>
          </div>
        </div>
      </section>

      <div className="user-form-actions">
        <Link to=".." className="button user-cancel-button">
          Cancel
        </Link>
        <button className="button user-submit-button" type="submit">
          <i className="fa-solid fa-check" aria-hidden="true" />
          {disableUsername ? "Save changes" : "Create user"}
        </button>
      </div>
    </form>
  );
};

const EditUserForm = ({ user, ...rest }: any) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...user,
      receiveDailyReports: Boolean(user.receiveDailyReports),
    },
  });
  return (
    <UserForm
      {...{ register, handleSubmit, errors, disableUsername: true, defaultValues: user, ...rest }}
    />
  );
};

const CreateUserForm = ({ ...rest }: any) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { receiveDailyReports: false } });
  return <UserForm {...{ register, handleSubmit, errors, ...rest }} />;
};

export const EditUser = () => {
  const { invokeApi, group } = useUser();
  const params = useParams();
  const navigate = useNavigate();

  const [users, setUsers] = useAtom(usersState);

  useEffect(() => {
    invokeApi({ endpoint: "/getUsers" }).then((users: any) => {
      setUsers(users);
    });
  }, []);

  const isCreateMode = Boolean(!params.username);
  const user: any =
    !isCreateMode &&
    users.users.find(({ username }: any) => username === params.username);

  const onSubmit = (data: any) => {
    invokeApi({
      endpoint: isCreateMode ? "/createUser" : "/updateUser",
      data,
    }).then(() => {
      if (isCreateMode) {
        notify.success("User successfully created!");
      } else {
        notify.success("User successfully updated!");
      }
      navigate(`/${group}/users`);
    });
  };

  return (
    <main className="content edit-user-page">
      <header className="edit-user-heading">
        <span className="edit-user-eyebrow">Administration</span>
        <h1>{isCreateMode ? "Create user" : "Edit user"}</h1>
        <p>
          {isCreateMode
            ? "Add an account and configure its access to aging reports."
            : `Manage account details and report access for ${params.username}.`}
        </p>
      </header>
      {!isCreateMode && user ? (
        <EditUserForm user={user} users={users} onSubmit={onSubmit} />
      ) : (
        <CreateUserForm users={users} onSubmit={onSubmit} />
      )}
    </main>
  );
};
